// Panel de la herramienta "Marca": atajos — paleta de colores de la marca
// (se aplica al texto/forma seleccionado, o al fondo) y colocar el logo.
// Los colores llegan ya normalizados por prop (useBrandColors en EditorShell).
// El logo sale de brand_profiles.logo_url (URL permanente, segura para embeber).

import { ImagePlus } from 'lucide-react';
import { loadImageRatio, makeImage } from '../lib/factory';
import { PanelSheet } from './PanelSheet';
import type { SceneApi } from '../scene/useScene';

interface Props {
  api: SceneApi;
  brandId: string | null;
  brandColors: string[];
  logoUrl: string | null;
  onClose: () => void;
}

export function MarcaPanel({ api, brandId, brandColors, logoUrl, onClose }: Props) {
  const applyColor = (c: string) => {
    const sel = api.selected;
    if (sel?.type === 'text') {
      api.checkpoint();
      api.updateLayer(sel.id, { color: c });
    } else if (sel?.type === 'shape') {
      api.checkpoint();
      api.updateLayer(sel.id, { fill: c });
    } else {
      api.setBg(c);
    }
  };

  const placeLogo = async () => {
    if (!logoUrl) return;
    const ratio = await loadImageRatio(logoUrl);
    api.addLayer(makeImage(logoUrl, api.scene.ratio, ratio));
  };

  return (
    <PanelSheet title="Marca" onClose={onClose}>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
        {!brandId ? (
          <p className="text-[13px] text-neutral-400 text-center mt-4">Necesitás una marca activa.</p>
        ) : (
          <>
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">Colores de la marca</p>
              {brandColors.length === 0 ? (
                <p className="text-[12.5px] text-neutral-400">Tu marca no tiene colores cargados.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {brandColors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        title={c}
                        onClick={() => applyColor(c)}
                        className="w-8 h-8 rounded-full border border-black/10 active:scale-95 transition"
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                  <p className="text-[11.5px] text-neutral-400 mt-2">
                    Se aplica al texto o forma seleccionado; si no hay nada seleccionado, al fondo.
                  </p>
                </>
              )}
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">Logo</p>
              {logoUrl ? (
                <button
                  type="button"
                  onClick={placeLogo}
                  className="w-full rounded-md border border-neutral-200 hover:border-brand-primary/40 hover:bg-surface-elevated p-2.5 flex items-center gap-3 transition-colors active:scale-[0.99]"
                >
                  <img
                    src={logoUrl}
                    alt="Logo de la marca"
                    className="w-12 h-12 object-contain rounded bg-neutral-50 border border-neutral-100 shrink-0"
                  />
                  <span className="text-[13.5px] text-neutral-700 font-medium inline-flex items-center gap-1.5">
                    <ImagePlus className="w-4 h-4" /> Colocar logo
                  </span>
                </button>
              ) : (
                <p className="text-[11.5px] text-neutral-400">Tu marca no tiene logo cargado.</p>
              )}
            </div>
          </>
        )}
      </div>
    </PanelSheet>
  );
}
